import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Center,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { CircleOutlineCheckIcon, ZipExportIcon } from "modules/icons";
import colors from "app/colors";
import {
  CreateJobMutation,
  CreateJobMutationVariables,
  JobQuery,
  JobQueryVariables,
  JobType,
} from "gql/graphql";
import CREATE_JOB_MUTATION from "./networking/create-job";
import JOB_QUERY from "./networking/job";
import { useOverlay } from "modules/providers/overlay-provider";

enum DisplayType {
  PROCESSING = "PROCESSING",
  ERROR = "ERROR",
  DOWNLOAD = "DOWNLOAD",
}

interface IProps {
  messageThreadId: string;
}

const DownloadAllFilesModal = ({ messageThreadId }: IProps) => {
  const { closeModal } = useOverlay();
  const [displayType, setDisplayType] = useState<DisplayType>();

  const [createJobMutation] = useMutation<
    CreateJobMutation,
    CreateJobMutationVariables
  >(CREATE_JOB_MUTATION);

  const [jobId, setJobId] = useState<string | null>();

  const { data } = useQuery<JobQuery, JobQueryVariables>(JOB_QUERY, {
    variables: {
      id: jobId!,
    },
    skip: !jobId,
  });

  const url = useMemo(() => data?.job?.url, [data]);

  const createJob = useCallback(async () => {
    const response = await createJobMutation({
      variables: {
        messageThreadId,
        type: JobType.MessageThreadFiles,
      },
    });
    if (response.data) {
      setJobId(response.data.createJob.id);
    }
  }, [createJobMutation, messageThreadId]);

  useEffect(() => {
    setDisplayType((previous) => {
      if (previous === undefined) {
        createJob();
        return DisplayType.PROCESSING;
      }
      return previous;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDownloadClicked = useCallback(() => {
    if (url) {
      const link = document.createElement("a");
      link.href = url;
      link.click();
      closeModal();
    }
  }, [closeModal, url]);

  const onCloseClicked = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      closeModal();
    },
    [closeModal],
  );

  useEffect(() => {
    if (url) setDisplayType(DisplayType.DOWNLOAD);
  }, [url]);

  return (
    <Modal isOpen onClose={closeModal} size="xs" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16} p={4}>
        <ModalHeader>
          <Center>
            <VStack>
              <ZipExportIcon boxSize={10} />
              <Text pt={2}>Download Files</Text>
            </VStack>
          </Center>
        </ModalHeader>
        <ModalBody p={0}>
          {displayType === DisplayType.PROCESSING && (
            <VStack w="full" alignItems="center">
              <HStack>
                <Spinner size="sm" />
                <Text fontSize={14}>ZIP processing, please wait...</Text>
              </HStack>
              <HStack spacing={4} p={4}>
                <Button
                  variant="cancel"
                  height={12}
                  w={28}
                  onClick={onCloseClicked}
                >
                  Cancel
                </Button>
                <Button height={12} w={28} isDisabled>
                  Download
                </Button>
              </HStack>
            </VStack>
          )}
          {displayType === DisplayType.DOWNLOAD && (
            <VStack w="full" alignItems="center">
              <Text fontSize={14} fontWeight={700}>
                <CircleOutlineCheckIcon
                  boxSize={4}
                  stroke={colors.greenPrimary}
                  mr={2}
                />
                ZIP ready for download
              </Text>
              <HStack spacing={4} p={4}>
                <Button
                  variant="cancel"
                  height={12}
                  w={28}
                  onClick={onCloseClicked}
                >
                  Cancel
                </Button>
                <Button height={12} w={28} onClick={onDownloadClicked}>
                  Download
                </Button>
              </HStack>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(DownloadAllFilesModal);
